import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Mail, Phone, ShieldAlert } from "lucide-react";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import { Seo, SITE_URL, ORG_ID, breadcrumbJsonLd } from "../components/Seo";
import { AuroraLight, Overline, PRIMARY, Reveal } from "../components/shared/kursusUi";
import { LangNotice, LangSwitcher } from "../components/lang/LangBits";
import { COURSE_KEYS, getLangPack, indexAlternates, langCoursePath } from "../data/lang";
import { CONTACT } from "../data/votraLocations";

const LangIndexPage = ({ lang }) => {
  const pack = getLangPack(lang);

  if (!pack) {
    return (
      <div className="min-h-screen flex flex-col" style={{ backgroundColor: "hsl(210, 20%, 98%)" }}>
        <Seo title="Page not found | Votra" description="The page does not exist." path="/" noindex />
        <Header />
        <div className="flex-1 flex flex-col items-center justify-center py-24 px-4 text-center">
          <h1 className="font-display text-3xl font-medium text-slate-900 mb-3">Page not found</h1>
          <Link to="/kurser" className="font-semibold hover:underline" style={{ color: PRIMARY }}>
            See all Votra courses
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const ui = pack.ui;
  const path = `/${pack.code}`;

  const jsonLd = [
    breadcrumbJsonLd([{ name: "Votra", path: "/" }, { name: ui.breadcrumb || pack.htmlLang, path }]),
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "@id": `${SITE_URL}${path}#page`,
      name: ui.indexH1,
      description: ui.indexDescription,
      url: `${SITE_URL}${path}`,
      inLanguage: pack.code,
      publisher: { "@id": ORG_ID },
    },
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "@id": `${SITE_URL}${path}#courses`,
      numberOfItems: COURSE_KEYS.length,
      itemListElement: COURSE_KEYS.map((k, i) => ({
        "@type": "ListItem",
        position: i + 1,
        item: {
          "@type": "Course",
          name: pack.courses[k].facts.course,
          description: pack.courses[k].short,
          url: `${SITE_URL}/${pack.code}/${encodeURIComponent(pack.courses[k].slug)}`,
          inLanguage: pack.code,
          provider: { "@id": ORG_ID },
        },
      })),
    },
  ];

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: "hsl(210, 20%, 98%)" }}>
      <div className="votra-grain" aria-hidden />
      <Seo
        title={ui.indexTitle}
        description={ui.indexDescription}
        path={path}
        lang={pack.htmlLang}
        locale={pack.locale}
        alternates={indexAlternates(SITE_URL)}
        jsonLd={jsonLd}
      />
      <div lang="da" dir="ltr">
        <Header />
      </div>
      <LangNotice pack={pack} />

      <div dir={pack.dir} lang={pack.htmlLang} data-testid={`lang-index-${pack.code}`}>
        <section className="hero-light relative overflow-hidden" style={{ backgroundColor: "#F4F6FB" }}>
          <AuroraLight />
          <div className="container mx-auto px-4 md:px-6 relative z-10 py-14 md:py-20">
            <div className="max-w-3xl">
              <Reveal i={1}><Overline light>{pack.nativeName}</Overline></Reveal>
              <Reveal i={2}>
                <h1 className="font-display text-4xl sm:text-5xl font-medium text-white leading-tight mb-5" data-testid="lang-index-h1">
                  {ui.indexH1}
                </h1>
              </Reveal>
              <Reveal i={3}>
                <p className="text-base md:text-lg text-slate-300 leading-relaxed">{ui.indexIntro}</p>
              </Reveal>
            </div>
          </div>
        </section>

        <div className="container mx-auto px-4 md:px-6 py-12 md:py-16 flex-1">
          <div className="max-w-4xl mx-auto space-y-8">
            <Reveal>
              <h2 className="font-display text-2xl md:text-3xl font-medium text-slate-900 mb-5">{ui.coursesHeading}</h2>              <div className="grid gap-4 sm:grid-cols-2">
                {COURSE_KEYS.map((k) => {
                  const c = pack.courses[k];
                  if (!c) return null;
                  return (
                    <Link
                      key={k}
                      to={langCoursePath(pack.code, k)}
                      className="group block rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 hover:border-[hsl(210,100%,45%)] hover:-translate-y-0.5 transition-all duration-300"
                      data-testid={`lang-course-card-${k}`}
                    >
                      {c.danishRequired && (
                        <span className="inline-flex items-center gap-1.5 mb-2 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-amber-100 text-amber-900">
                          <ShieldAlert className="h-3.5 w-3.5" /> {ui.danishRequiredBadge}
                        </span>
                      )}
                      <h3 className="font-semibold text-slate-900 text-lg leading-snug">{c.facts.course}</h3>
                      <p className="mt-2 text-sm text-slate-600 leading-relaxed">{c.short}</p>
                      <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold" style={{ color: PRIMARY }}>
                        {ui.openCourse}
                        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                      </span>
                    </Link>
                  );
                })}
              </div>
              <Link
                to={`/kalender?sprog=${pack.code}`}
                className="inline-flex items-center gap-1.5 mt-5 text-sm font-semibold hover:underline"
                style={{ color: PRIMARY }}
                data-testid="lang-index-calendar-link"
              >
                {ui.dates.allInLanguage} <ArrowRight className="h-4 w-4" />
              </Link>
            </Reveal>

            <Reveal className="rounded-2xl p-6 sm:p-8 text-white" style={{ background: "linear-gradient(120deg, #0A192F, #123A6B)" }} data-testid="lang-index-cta">
              <h2 className="font-display text-2xl md:text-3xl font-medium mb-2">{ui.cta.heading}</h2>
              <p className="text-slate-300 leading-relaxed">{ui.cta.text}</p>
              <div className="mt-6 flex flex-wrap gap-3">
                <a
                  href={`tel:${CONTACT.phone}`}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-white"
                  style={{ backgroundColor: PRIMARY }}
                  data-testid="lang-index-call"
                >
                  <Phone className="h-4 w-4" /> {ui.cta.call}
                </a>
                <a
                  href={`mailto:${CONTACT.email}`}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold border border-white/25 bg-white/5 hover:bg-white/10 transition-colors"
                  data-testid="lang-index-email"
                >
                  <Mail className="h-4 w-4" /> {ui.cta.email}
                </a>
              </div>
            </Reveal>

            <LangSwitcher pack={pack} courseKey={null} danishPath="/kurser" danishLabel="Dansk" />
          </div>
        </div>
      </div>

      <div lang="da" dir="ltr">
        <Footer />
      </div>
    </div>
  );
};

export default LangIndexPage;
